import get from 'lodash/get';
import map from 'lodash/map';
import omit from 'lodash/omit';
import { PropTypes } from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import { change, getFormSyncErrors, submit } from 'redux-form';
import { fetchSites, hideDialog, showDialog } from '../../../core/actions';
import { Drawer } from '../../../core/components/Drawer';
import { checkErrors } from '../../../core/formValidation';
import * as toastMessages from '../../../core/toastMessages';
import { selectApiRequests } from '../../../store/apiRequestReducer';
import {
  selectOperatorGroup,
  selectRole,
  selectSite,
  selectUser,
} from '../../../store/authReducer';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import { selectSites } from '../../site-selection/reducer';
import { selectSiteSettingsSite } from '../../settings/reducer';
import * as actions from '../actions';
import ReclamationForm from '../components/ReclamationForm';
import Reclamation, { PluralReclamationType, ReclamationStatus, ReclamationType } from '../models';
import { selectFromDate, selectReclamationDamageOptions, selectUntilDate } from '../reducer';

export class ReclamationFormWrapper extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({}).isRequired,
    type: PropTypes.number,
    triggerSubmit: PropTypes.func.isRequired,
    initialReclamation: PropTypes.shape({
      status: PropTypes.number.isRequired,
    }).isRequired, // instanceOf Reclamation
    fetchEmployees: PropTypes.func.isRequired,
    fetchSites: PropTypes.func.isRequired,
    getDamageClaimOptions: PropTypes.func.isRequired,
    reclamationDamageOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        value: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            value: PropTypes.string,
          })
        ),
      })
    ).isRequired,
    sites: PropTypes.arrayOf(PropTypes.shape({})),
    employees: PropTypes.arrayOf(PropTypes.shape({})),
    currentUser: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isLoading: PropTypes.bool,
    isNew: PropTypes.bool,
    completeMode: PropTypes.bool,
    useInternalDrawer: PropTypes.bool,
    drawerMaximized: PropTypes.bool,
    onCloseDrawer: PropTypes.func,
    onClaimCreated: PropTypes.func,
    showDialog: PropTypes.func.isRequired,
    hideDialog: PropTypes.func.isRequired,
    onMaximizeDrawer: PropTypes.func,
    addReclamation: PropTypes.func.isRequired,
    saveReclamation: PropTypes.func,
    addFile: PropTypes.func.isRequired,
    role: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    operatorGroup: PropTypes.shape({}),
    siteSettingsSite: PropTypes.shape({}),
    change: PropTypes.func.isRequired,
    history: PropTypes.shape({
      goBack: PropTypes.func.isRequired,
      push: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        site: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  };

  static defaultProps = {
    isNew: true,
    completeMode: false,
    useInternalDrawer: true,
  };

  state = {
    wizardDrawerExpanded: false,
    wizardDrawerMaximized: true,
    wizardDrawerMaximizedOffset: 0,
    showWizardDrawerMaximize: true,
  };

  componentDidMount() {
    if (this.props.isNew) {
      this.props.deleteReclamationInEdit();
    }
    this.props.fetchEmployees();
    this.props.fetchSites();
    this.props.getDamageClaimOptions();
    this.observeSideNavWidth();
    this.openWizardDrawerTimeout = window.setTimeout(() => {
      this.setState({ wizardDrawerExpanded: true });
    }, 0);
  }

  componentWillUnmount() {
    window.clearTimeout(this.openWizardDrawerTimeout);
    window.clearTimeout(this.closeWizardDrawerTimeout);
    window.removeEventListener('resize', this.updateWizardDrawerMaximizedOffset);
    if (this.sideNavResizeObserver) {
      this.sideNavResizeObserver.disconnect();
    }
  }

  onSubmit = (data) => {
    // The form starts from the full claim, but its related records are saved through their own
    // endpoints. Sending them back makes the backend validate them too, so a file without a
    // description would block saving any edit.
    const reclamation = omit(data.reclamation || {}, [
      'site',
      'files',
      'notes',
      'repairItems',
      'history',
    ]);
    const files = data.files || [];
    const operatorGroupDetails = this.props.operatorGroup?.details || {};
    const operatorGroupPayload = this.props.operatorGroup
      ? {
          name: this.props.operatorGroup.name,
          details: {
            email: operatorGroupDetails.email || this.props.operatorGroup.email,
            emailSendingDomain:
              operatorGroupDetails.emailSendingDomain ||
              this.props.operatorGroup.emailSendingDomain,
          },
        }
      : null;
    const venuePayload = this.props.siteSettingsSite || reclamation.issueSite;
    if (
      (this.props.isNew || this.props.completeMode) &&
      this.props.type === ReclamationType.Claim &&
      reclamation.status === ReclamationStatus.Open
    ) {
      reclamation.operatorGroup = operatorGroupPayload;
      reclamation.venue = venuePayload;
      reclamation.type = 'Claim';
    }

    const originalNavigate = this.getNavigationFunction();
    const navigate = (response) => {
      const reclamationId = response?.id || reclamation.id;
      if (files.length && reclamationId) {
        files.forEach((file) => {
          const formData = new FormData();
          formData.append('file', file);
          this.props.addFile({
            siteId: reclamation.issueSiteId,
            reclamationId,
            file: formData,
          });
        });
      }
      originalNavigate(response);
    };
    return (this.props.isNew ? this.props.addReclamation : this.props.saveReclamation)({
      reclamation,
      navigate,
    });
  };

  getNavigationFunction = () => {
    const { site } = this.props.match.params;
    return this.props.type === ReclamationType.Claim
      ? (response) => {
          if (this.props.isNew) {
            toastMessages.generateToastr(
              'success',
              'Claim Submitted',
              'The claim was submitted successfully.'
            );
            if (this.props.onClaimCreated) {
              this.props.onClaimCreated(response);
            } else {
              this.props.history.push(`/${site}/claims`);
            }
          } else {
            // Navigate back to claims table with drawer open
            this.props.history.push({
              pathname: `/${site}/claims`,
              state: { openDrawerId: response.id },
            });
          }
        }
      : () => {
          this.props.history.push(`/${site}/rewashes`);
        };
  };

  title = () => {
    const { intl } = this.props;
    switch (this.props.type) {
      case ReclamationType.Rewash:
        return this.props.isNew
          ? intl.formatMessage({ id: 'addRewash' })
          : intl.formatMessage({ id: 'editRewash' });
      case ReclamationType.Claim:
      default:
        return this.props.isNew
          ? intl.formatMessage({ id: 'addClaim' })
          : intl.formatMessage({ id: 'editClaim' });
    }
  };

  /*
   * The state of affectedArea doesn't update when carPart is changed.
   * This function fixes that.
   */
  handleChange = (values) => {
    if (this.props.type === ReclamationType.Claim) {
      const carPartData = this.props.reclamationDamageOptions.find(
        (part) => part.id === values.claimDamagePartId
      );
      const options = carPartData && map(carPartData.options, 'id');

      if (options && !options.includes(values.claimDamageAreaId)) {
        this.props.change('reclamation', 'claimDamageAreaId', options[0] || null);
      }
    }
  };

  handleCloseWizardDrawer = () => {
    this.props.showDialog('CONFIRM_DIALOG', {
      title: 'Discard claim?',
      text: this.props.intl.formatMessage({ id: 'claimWizardDiscardProgressConfirmation' }),
      submitButtonText: 'Discard Claim',
      onReady: this.discardWizardDrawer,
    });
  };

  discardWizardDrawer = () => {
    this.props.hideDialog();
    if (this.props.onCloseDrawer) {
      this.props.onCloseDrawer();
      return;
    }
    const { site } = this.props.match.params;
    this.setState({ wizardDrawerExpanded: false });
    this.closeWizardDrawerTimeout = window.setTimeout(() => {
      this.props.history.push(`/${site}/claims`);
    }, 350);
  };

  handleMaximizeWizardDrawer = () => {
    if (this.props.onMaximizeDrawer) {
      this.props.onMaximizeDrawer();
      return;
    }
    this.setState((prevState) => ({
      wizardDrawerMaximized: !prevState.wizardDrawerMaximized,
    }));
  };

  getSideNavWidth = () => {
    if (window.innerWidth <= 700) return 0;
    const nav = document.querySelector('nav.main');
    return nav ? Math.round(nav.getBoundingClientRect().width) : 0;
  };

  updateWizardDrawerMaximizedOffset = () => {
    const nextOffset = this.getSideNavWidth();
    const showWizardDrawerMaximize = window.innerWidth > 700;
    const nextState = {};
    if (nextOffset !== this.state.wizardDrawerMaximizedOffset) {
      nextState.wizardDrawerMaximizedOffset = nextOffset;
    }
    if (showWizardDrawerMaximize !== this.state.showWizardDrawerMaximize) {
      nextState.showWizardDrawerMaximize = showWizardDrawerMaximize;
      if (!showWizardDrawerMaximize && this.state.wizardDrawerMaximized) {
        nextState.wizardDrawerMaximized = false;
      }
    }
    if (Object.keys(nextState).length) {
      this.setState(nextState);
    }
  };

  observeSideNavWidth = () => {
    window.addEventListener('resize', this.updateWizardDrawerMaximizedOffset);
    this.updateWizardDrawerMaximizedOffset();

    const nav = document.querySelector('nav.main');
    if (nav && window.ResizeObserver) {
      this.sideNavResizeObserver = new window.ResizeObserver(
        this.updateWizardDrawerMaximizedOffset
      );
      this.sideNavResizeObserver.observe(nav);
    }
  };

  render() {
    const initialReclamation = {
      ...this.props.initialReclamation,
    };
    if (
      !this.props.isNew &&
      !this.props.completeMode &&
      initialReclamation.status === ReclamationStatus.Incomplete
    ) {
      initialReclamation.status = ReclamationStatus.Open;
    }

    const shouldRenderClaimWizardDrawer =
      this.props.type === ReclamationType.Claim && (this.props.isNew || this.props.completeMode);
    const wizardDrawerMaximized =
      this.props.drawerMaximized !== undefined
        ? this.props.drawerMaximized
        : this.state.wizardDrawerMaximized;

    const form = (
      <ReclamationForm
        type={this.props.type}
        onSubmit={(reclamation, files) => this.onSubmit({ reclamation, files, page: 0 })}
        initialValues={initialReclamation}
        employees={this.props.employees}
        currentUser={this.props.currentUser}
        sites={this.props.sites}
        title={this.title()}
        role={this.props.role}
        onReady={() => {
          this.props.triggerSubmit('reclamation');
        }}
        onChange={this.handleChange}
        disabled={checkErrors(this.props.errors) || this.props.isLoading}
        isSubmitting={this.props.isLoading}
        isNew={this.props.isNew}
        completeMode={this.props.completeMode}
        drawerMode={shouldRenderClaimWizardDrawer}
        drawerMaximized={wizardDrawerMaximized}
        onMaximizeDrawer={
          this.state.showWizardDrawerMaximize ? this.handleMaximizeWizardDrawer : null
        }
        onCloseDrawer={this.handleCloseWizardDrawer}
        enableReinitialize
      />
    );

    if (shouldRenderClaimWizardDrawer && this.props.useInternalDrawer) {
      return (
        <Drawer
          expanded={this.state.wizardDrawerExpanded}
          maximized={wizardDrawerMaximized}
          maximizedOffset={this.state.wizardDrawerMaximizedOffset}
          expandedWidth={70}
          overlay
        >
          <div className="claim-wizard-drawer-content">{form}</div>
        </Drawer>
      );
    }

    return form;
  }
}

const mapStateToProps = (state, ownProps) => {
  const type = ownProps.location.pathname.split('/')[2];
  const employees = selectEmployees(state);
  const apiRequests = selectApiRequests(state);
  const isLoading = get(apiRequests, `${actions.ADD_RECLAMATION}.isLoading`, false);
  const reclamationDamageOptions = selectReclamationDamageOptions(state);
  const initialFormValues = {
    status: ReclamationStatus.Incomplete,
    type: PluralReclamationType[type],
    issueSite: { id: Number(selectSite(state)) },
  };

  if (PluralReclamationType[type] === ReclamationType.Claim && reclamationDamageOptions.length) {
    initialFormValues.claimDamagePartId = reclamationDamageOptions[0].id;
    if (reclamationDamageOptions[0].options.length) {
      initialFormValues.claimDamageAreaId = reclamationDamageOptions[0].options[0].id;
    }
  }

  const { issueSite, ...otherProps } = ownProps.initialReclamation
    ? ownProps.initialReclamation
    : new Reclamation(initialFormValues);

  return {
    type: PluralReclamationType[type],
    initialReclamation: { issueSiteId: issueSite.id, ...otherProps },
    employees,
    currentUser: selectUser(state),
    sites: selectSites(state),
    role: selectRole(state),
    operatorGroup: selectOperatorGroup(state),
    siteSettingsSite: selectSiteSettingsSite(state),
    errors: getFormSyncErrors('reclamation')(state),
    fromDate: selectFromDate(state),
    untilDate: selectUntilDate(state),
    reclamationDamageOptions,
    isLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      ...actions,
      triggerSubmit: submit,
      fetchEmployees,
      fetchSites,
      change,
      showDialog,
      hideDialog,
    },
    dispatch
  );
};

export default withRouter(
  compose(connect(mapStateToProps, mapDispatchToProps), injectIntl)(ReclamationFormWrapper)
);
